import { dossierApi } from '../api/resources';
import { idDefinitif } from './identifiants';
import { COTE_MAX, reduitLImage } from './image';
import { TAILLE_MAX_PIECE, enMo } from './medias';

/**
 * Carte d'identité et carte AVS de l'employé, dans son dossier.
 *
 * <p>Le bloc « 📎 Pièces de l'employé » de la fiche ne garde rien à part : il
 * lit et écrit les pièces du dossier (`documents`), celles que la liste
 * « 📁 Dossier documents » montre plus bas. Une pièce d'identité ou une carte
 * AVS ajoutée d'un côté se voit de l'autre.
 *
 * <p>Un fichier ne se joint qu'à une pièce que le serveur connaît. Choisi sur
 * une fiche pas encore enregistrée (ou pour une pièce encore à créer), il
 * attend sur la pièce (`aJoindre`) ; la fiche enregistrée, la pièce reçoit son
 * identifiant et le fichier part au dossier ({@link envoieLesPiecesEnAttente}).
 */

/**
 * Prépare le fichier d'une pièce avant son envoi : une photo est ramenée à une
 * taille raisonnable, puis le poids est vérifié contre la limite du serveur.
 *
 * @param {File} fichier fichier choisi
 * @returns {Promise<Blob>} le fichier à envoyer
 * @throws {Error} si le fichier dépasse la taille admise
 */
export async function prepareLaPiece(fichier) {
  const reduit = await reduitLImage(fichier, COTE_MAX);
  if (reduit.size > TAILLE_MAX_PIECE) {
    throw new Error(
      `Ce fichier pèse ${enMo(reduit.size)} Mo, au-delà des ${enMo(TAILLE_MAX_PIECE)} Mo admis.`,
    );
  }
  return reduit;
}

/** Type de la pièce d'identité, tel que la liste du dossier l'écrit. */
export const TYPE_IDENTITE = "Pièce d'identité";
/** Type de la carte AVS. */
export const TYPE_AVS = 'Carte AVS';
/** Nom de la pièce qui porte le recto (le dossier tient un fichier par pièce). */
export const NOM_RECTO = 'Recto';
/** Nom de la pièce qui porte le verso. */
export const NOM_VERSO = 'Verso';

const estVerso = (doc) => /verso/i.test(String(doc?.nom || ''));

/**
 * @param {object} doc pièce du dossier
 * @returns {boolean} vrai si un fichier y est joint, ou attend d'y partir
 */
export function aUnFichier(doc) {
  return doc?.fichier === true || !!doc?.aJoindre;
}

/**
 * Où en sont les deux pièces : la pièce qui porte le recto (une pièce
 * d'identité qui n'est pas le verso : un passeport scanné en une fois compte
 * aussi), celle du verso, la carte AVS.
 *
 * @param {object[]} [documents] pièces du dossier
 * @returns {{recto: number, verso: number, avs: number}} la position de chaque
 *   pièce avec fichier, -1 si elle manque
 */
export function etatDesPieces(documents) {
  const l = documents || [];
  return {
    recto: l.findIndex((d) => d?.type === TYPE_IDENTITE && !estVerso(d) && aUnFichier(d)),
    verso: l.findIndex((d) => d?.type === TYPE_IDENTITE && estVerso(d) && aUnFichier(d)),
    avs: l.findIndex((d) => d?.type === TYPE_AVS && aUnFichier(d)),
  };
}

/**
 * La pièce qui reçoit le fichier choisi : celle qui porte déjà ce côté, sinon
 * une pièce du même nom encore sans fichier.
 *
 * @param {object[]} [documents] pièces du dossier
 * @param {'recto'|'verso'|'avs'} cote ce que le fichier montre
 * @returns {number} sa position, -1 s'il faut la créer
 */
export function pieceDuCote(documents, cote) {
  const l = documents || [];
  const deja = etatDesPieces(l)[cote];
  if (deja >= 0) return deja;
  if (cote === 'avs') return l.findIndex((d) => d?.type === TYPE_AVS);
  const nom = cote === 'verso' ? NOM_VERSO : NOM_RECTO;
  return l.findIndex(
    (d) => d?.type === TYPE_IDENTITE && String(d.nom || '').toLowerCase() === nom.toLowerCase(),
  );
}

/**
 * La pièce à créer pour ce côté.
 *
 * @param {'recto'|'verso'|'avs'} cote ce que le fichier montre
 * @returns {object} la pièce, sans identifiant
 */
export function nouvellePiece(cote) {
  return {
    type: cote === 'avs' ? TYPE_AVS : TYPE_IDENTITE,
    nom: cote === 'avs' ? '' : cote === 'verso' ? NOM_VERSO : NOM_RECTO,
    exp: '',
    statut: 'reçu',
    parLeBloc: true,
  };
}

/**
 * Les pièces qui manquent à un employé en poste, d'après son dossier.
 *
 * @param {object} employe fiche de l'employé
 * @returns {('identite'|'avs')[]} ce qui manque, dans l'ordre de la fiche
 */
export function piecesManquantes(employe) {
  const docs = employe?.documents || [];
  const manque = [];
  if (!docs.some((d) => d?.type === TYPE_IDENTITE && aUnFichier(d))) manque.push('identite');
  if (!docs.some((d) => d?.type === TYPE_AVS && aUnFichier(d))) manque.push('avs');
  return manque;
}

// Fichiers choisis avant que leur pièce n'existe au serveur, par
// enregistrement de fiche. Hors de l'écran : quitter « Équipe » juste après
// l'enregistrement ne les perd pas, ils partent au retour.
const enAttente = new Map();
let prochain = 1;

/**
 * Retient les fichiers d'une fiche qu'on enregistre, le temps que ses pièces
 * reçoivent leur identifiant.
 *
 * @param {number|string} idFiche identifiant de la fiche, provisoire pour une nouvelle
 * @param {object[]} [documents] pièces de la fiche telle qu'enregistrée
 * @returns {boolean} vrai si des fichiers attendent
 */
export function retientLesPieces(idFiche, documents) {
  const l = documents || [];
  const pieces = l
    .filter((d) => d?.aJoindre && typeof d.id !== 'number')
    .map((d) => ({ type: d.type, nom: d.nom || '', aJoindre: d.aJoindre }));
  if (pieces.length === 0) return false;
  const connus = new Set(l.map((d) => d?.id).filter((id) => typeof id === 'number'));
  enAttente.set(prochain++, { idFiche, connus, pieces, enCours: false });
  return true;
}

/**
 * @param {number|string} idFiche identifiant de la fiche, provisoire ou du serveur
 * @returns {boolean} vrai si des fichiers de cette fiche attendent encore
 */
export function attendDesPieces(idFiche) {
  for (const a of enAttente.values()) {
    if (a.idFiche === idFiche || idDefinitif(a.idFiche) === idFiche) return true;
  }
  return false;
}

/** Oublie les fichiers en attente (tests). */
export function oublieLesPiecesEnAttente() {
  enAttente.clear();
}

/**
 * Envoie au dossier les fichiers dont la pièce vient de recevoir son
 * identifiant. Sans rien à faire tant que le serveur n'a pas rendu la fiche
 * avec ses nouvelles pièces : l'appel suivant, à la prochaine relecture,
 * reprend.
 *
 * @param {object[]} employes fiches telles que l'écran les tient
 * @param {object} rappels suites de l'envoi
 * @param {(employeId: number, pieces: Map<number, object>) => void} rappels.surEnvoi
 *   pièces mises à jour, par identifiant, une fois les fichiers joints
 * @param {(employe: object) => void} [rappels.surErreur] un fichier n'est pas parti
 * @returns {Promise<void>} fin des envois lancés
 */
export async function envoieLesPiecesEnAttente(employes, { surEnvoi, surErreur }) {
  const envois = [];
  for (const [cle, attente] of enAttente) {
    if (attente.enCours) continue;
    const id = typeof attente.idFiche === 'number' ? attente.idFiche : idDefinitif(attente.idFiche);
    if (typeof id !== 'number') continue;
    const emp = (employes || []).find((e) => e.id === id);
    if (!emp) continue;
    const nouvelles = (emp.documents || []).filter(
      (d) => typeof d?.id === 'number' && !attente.connus.has(d.id),
    );
    const prises = new Set();
    const paires = [];
    for (const p of attente.pieces) {
      const doc = nouvelles.find(
        (d) => !prises.has(d.id) && d.type === p.type && (d.nom || '') === p.nom,
      );
      if (!doc) break;
      prises.add(doc.id);
      paires.push([p, doc]);
    }
    // Le serveur n'a pas encore rendu toutes les pièces : on attend.
    if (paires.length !== attente.pieces.length) continue;
    attente.enCours = true;
    envois.push(
      (async () => {
        const majs = new Map();
        let echec = false;
        for (const [p, doc] of paires) {
          try {
            const dto = await dossierApi.joindre(id, doc.id, p.aJoindre.fichier, p.aJoindre.nom);
            majs.set(doc.id, {
              ...doc,
              fichier: dto?.fichierJoint === true,
              nomFichier: dto?.nomFichier || '',
            });
          } catch {
            echec = true;
          }
        }
        enAttente.delete(cle);
        if (majs.size > 0) surEnvoi(id, majs);
        if (echec && surErreur) surErreur(emp);
      })(),
    );
  }
  await Promise.all(envois);
}
